import React, { useMemo, useState, useCallback } from "react";
import { DateTime } from "luxon";
import { useArtist } from "../../../context/ArtistContext";
import { useArtistData } from "../../../context/ArtistDataContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import useEarnings from "../hooks/useEarnings";
import AverageCards from "../components/earnings/AverageCards";
import BarEarningsBreakdown from "../components/earnings/BarEarningsBreakdown";
import EarningsTable from "../components/earnings/EarningsTable";
import ShowCardModal from "../components/ShowCardModal";
import {
  civilDateTodayInZone,
  compareCivilYmd,
  normalizeBarTimeZone,
  parseCivilDateOnly,
} from "../../../utils/civilDateTime.js";

const venueYearStartYmd = (tz) =>
  DateTime.now().setZone(normalizeBarTimeZone(tz)).startOf("year").toISODate();

const venueDaysAgoYmd = (tz, days) =>
  DateTime.now()
    .setZone(normalizeBarTimeZone(tz))
    .minus({ days })
    .toISODate();

const venueLastCalendarYearBounds = (tz) => {
  const z = normalizeBarTimeZone(tz);
  const prior = DateTime.now().setZone(z).minus({ years: 1 });
  return {
    startYmd: prior.startOf("year").toISODate(),
    endYmd: prior.endOf("year").toISODate(),
  };
};

// Table filter options
const TABLE_FILTER_YTD = "ytd";
const TABLE_FILTER_LAST30 = "last30";
const TABLE_FILTER_LAST_YEAR = "lastYear";
const TABLE_FILTER_ALL = "all";

const Earnings = () => {
  const { selectedArtist } = useArtist();
  const { invalidateCache } = useArtistData();

  const deviceZone =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;
  const fetchEndYmd = civilDateTodayInZone(normalizeBarTimeZone(deviceZone));
  const fetchStartYmd = DateTime.fromISO(fetchEndYmd)
    .minus({ days: 730 })
    .toISODate();

  const {
    earnings: earningsData,
    loading: earningsLoading,
    refetch,
  } = useEarnings({
    startDate: fetchStartYmd,
    endDate: fetchEndYmd,
  });

  // Show Card modal: row click opens modal for editing earnings
  const [selectedShowForModal, setSelectedShowForModal] = useState(null);
  const [isShowModalOpen, setIsShowModalOpen] = useState(false);

  const handleEarningsUpdate = useCallback(() => {
    invalidateCache("earnings", selectedArtist?.artist_id ?? null);
    refetch();
  }, [invalidateCache, refetch, selectedArtist?.artist_id]);

  const closeShowModal = useCallback(() => {
    setIsShowModalOpen(false);
    setSelectedShowForModal(null);
  }, []);

  // Build fallbackShow for ShowCardModal (row + status; stage_name from row when "All", else selectedArtist)
  const fallbackShowForModal = useMemo(() => {
    if (!selectedShowForModal) return null;
    const stageName =
      selectedShowForModal.stage_name ?? selectedArtist?.stage_name ?? "";
    return {
      ...selectedShowForModal,
      status: "accepted",
      stage_name: stageName,
    };
  }, [selectedShowForModal, selectedArtist?.stage_name]);

  // Past shows only (date < today) — single source for averages, bar breakdown, table
  const pastEarnings = useMemo(() => {
    if (!earningsData || earningsData.length === 0) return [];
    return earningsData
      .filter((e) => {
        const tz = normalizeBarTimeZone(e.bar_time_zone);
        const todayVenue = civilDateTodayInZone(tz);
        const showYmd = parseCivilDateOnly(e.date);
        return compareCivilYmd(showYmd, todayVenue) < 0;
      })
      .sort((a, b) => compareCivilYmd(b.date, a.date));
  }, [earningsData]);

  // Last 365 days of past earnings for Average cards (Average Day / Week / Month)
  const earningsForAverages = useMemo(() => {
    return pastEarnings.filter((e) => {
      const tz = normalizeBarTimeZone(e.bar_time_zone);
      const cutoffYmd = venueDaysAgoYmd(tz, 365);
      const showYmd = parseCivilDateOnly(e.date);
      return compareCivilYmd(showYmd, cutoffYmd) >= 0;
    });
  }, [pastEarnings]);

  // Year-to-date: past earnings from Jan 1 of current year through today
  const earningsYTD = useMemo(() => {
    return pastEarnings.filter((e) => {
      const tz = normalizeBarTimeZone(e.bar_time_zone);
      const yStart = venueYearStartYmd(tz);
      const showYmd = parseCivilDateOnly(e.date);
      return compareCivilYmd(showYmd, yStart) >= 0;
    });
  }, [pastEarnings]);

  // Table filter: default Year to date
  const [tableFilter, setTableFilter] = useState(TABLE_FILTER_YTD);

  const tableShows = useMemo(() => {
    if (!pastEarnings.length) return [];

    return pastEarnings.filter((e) => {
      const tz = normalizeBarTimeZone(e.bar_time_zone);
      const showYmd = parseCivilDateOnly(e.date);
      if (tableFilter === TABLE_FILTER_ALL) return true;
      if (tableFilter === TABLE_FILTER_YTD) {
        return compareCivilYmd(showYmd, venueYearStartYmd(tz)) >= 0;
      }
      if (tableFilter === TABLE_FILTER_LAST30) {
        return compareCivilYmd(showYmd, venueDaysAgoYmd(tz, 30)) >= 0;
      }
      if (tableFilter === TABLE_FILTER_LAST_YEAR) {
        const { startYmd, endYmd } = venueLastCalendarYearBounds(tz);
        return (
          compareCivilYmd(showYmd, startYmd) >= 0 &&
          compareCivilYmd(showYmd, endYmd) <= 0
        );
      }
      return true;
    });
  }, [pastEarnings, tableFilter]);

  // Unique bars from past earnings (for table totals label / BarSelector if needed)
  const availableBars = useMemo(() => {
    const barMap = new Map();
    pastEarnings.forEach((e) => {
      if (e.bar_id != null && e.bar_name) {
        if (!barMap.has(e.bar_id)) {
          barMap.set(e.bar_id, { bar_id: e.bar_id, name: e.bar_name });
        }
      }
    });
    return Array.from(barMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [pastEarnings]);

  const pageTitle = useMemo(() => {
    if (selectedArtist === null) return "Earnings - All Artists";
    return `Earnings - ${selectedArtist.stage_name}`;
  }, [selectedArtist]);

  return (
    <DashboardLayout>
      <div className="earnings-page">
        {/* Header */}
        <div className="earnings-page-header">
          <h1 className="earnings-page-title">{pageTitle}</h1>
        </div>

        {/* Average Cards Section */}
        <div className="financial-section">
          <div className="financial-section-header">
            <h2 className="financial-section-title">Earnings overview</h2>
            <span
              className="info-icon financial-section-info-icon"
              data-tooltip="Average Day, Average Week, and Average Month are calculated from your past shows in the last 365 days. Year to date shows your total earnings from January 1st of this year through today (past shows only)."
              aria-label="How these values are calculated"
            >
              ?
            </span>
          </div>
          {earningsLoading ? (
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Average Day</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Average Week</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Average Month</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="financial-card">
                <h3>Year to date</h3>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Loading...</span>
                    <span className="slot-amount">
                      <span className="spinner" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <AverageCards
              earnings={earningsForAverages}
              earningsYTD={earningsYTD}
            />
          )}
        </div>

        {/* Bar-specific earnings section */}
        <div className="earnings-bar-section">
          <div className="earnings-table-header">
            <h2 className="section-title">Earnings by Bar</h2>
            <div className="earnings-table-filter">
              <label htmlFor="earnings-bar-filter-select">Show: </label>
              <select
                id="earnings-bar-filter-select"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                aria-label="Filter earnings by bar by time range"
              >
                <option value={TABLE_FILTER_YTD}>Year to date</option>
                <option value={TABLE_FILTER_LAST30}>Last 30 days</option>
                <option value={TABLE_FILTER_LAST_YEAR}>Last year</option>
                <option value={TABLE_FILTER_ALL}>All time</option>
              </select>
            </div>
          </div>
          <BarEarningsBreakdown pastEarnings={tableShows} />
        </div>

        {/* Past Shows Table Section */}
        <div className="earnings-table-section">
          <div className="earnings-table-header">
            <h2 className="section-title">Past Shows</h2>
            <div className="earnings-table-filter">
              <label htmlFor="earnings-table-filter-select">Show: </label>
              <select
                id="earnings-table-filter-select"
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
                aria-label="Filter past shows by time range"
              >
                <option value={TABLE_FILTER_YTD}>Year to date</option>
                <option value={TABLE_FILTER_LAST30}>Last 30 days</option>
                <option value={TABLE_FILTER_LAST_YEAR}>Last year</option>
                <option value={TABLE_FILTER_ALL}>All time</option>
              </select>
            </div>
          </div>
          <EarningsTable
            shows={tableShows}
            selectedBarId={null}
            availableBars={availableBars}
            onRowClick={(row) => {
              setSelectedShowForModal(row);
              setIsShowModalOpen(true);
            }}
          />
        </div>
      </div>
      <ShowCardModal
        isOpen={isShowModalOpen}
        onClose={closeShowModal}
        showId={selectedShowForModal?.show_id}
        fallbackShow={fallbackShowForModal}
        onEarningsUpdate={handleEarningsUpdate}
      />
    </DashboardLayout>
  );
};

export default Earnings;
